import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import Cart from './components/Cart/Cart';
import { CartProvider } from './context/CartContext';
import HomePage from './pages/HomePage/HomePage';
import ProductsPage from './pages/ProductsPage/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage/ProductDetailPage';
import AboutPage from './pages/AboutPage/AboutPage';
import ContactPage from './pages/ContactPage/ContactPage';
import AdminPage from './pages/AdminPage/AdminPage';
import LoginPage from './pages/LoginPage/LoginPage';
import SEO from './components/SEO/SEO';
import YandexMetrika from './components/YandexMetrika/YandexMetrika';
import { authAPI } from './services/api';
import './App.css';
import './styles/royal-platinum.css';
import './styles/alaport-theme.css';
import './i18n/config';

// Layout component to conditionally render Header/Footer
const Layout: React.FC<{ children: React.ReactNode; isAuthenticated: boolean; onLogout: () => void }> = ({
  children,
  isAuthenticated,
  onLogout
}) => {
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';

  return (
    <div className="App">
      <SEO />
      <YandexMetrika counterId={12345678} />
      <ScrollToTop />
      {!isLoginPage && <Header isAdmin={isAuthenticated} onLogout={onLogout} />}
      <Cart />
      <main className={`main-content ${isLoginPage ? 'no-header' : ''}`}>
        {children}
      </main>
      {!isLoginPage && <Footer />}
    </div>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const hasAuthFlag = localStorage.getItem('isAdminAuthenticated') === 'true';
    const hasToken = Boolean(localStorage.getItem('admin_token'));
    return hasAuthFlag && hasToken;
  });

  // Force Alaport theme (theme-7) 
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'theme-7');
    localStorage.setItem('selectedTheme', 'theme-7');
  }, []);

  useEffect(() => {
    const verifyAuth = async () => {
      if (!isAuthenticated) return;

      try {
        await authAPI.verifyToken();
      } catch (error) {
        console.error('Auth token verification failed:', error);
        localStorage.removeItem('isAdminAuthenticated');
        localStorage.removeItem('adminUser');
        localStorage.removeItem('admin_token');
        setIsAuthenticated(false);
      }
    };

    verifyAuth();
  }, [isAuthenticated]);

  const handleLogin = (success: boolean) => {
    setIsAuthenticated(success);
  };

  const handleLogout = () => {
    localStorage.removeItem('isAdminAuthenticated');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
  };

  return (
    <HelmetProvider>
      <CartProvider>
        <Router>
          <Layout isAuthenticated={isAuthenticated} onLogout={handleLogout}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/products/:id" element={<ProductDetailPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <AdminPage />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Layout>
        </Router>
      </CartProvider>
    </HelmetProvider>
  );
}

export default App;
